/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Page dashboard translation constants
 *
 * @author Nikolay Sviridov
 * @version 1.34.0
 * @since 1.34.0
 */

import { ChangeEvent, useMemo } from 'react';

import { IconButton, Switch, Typography } from '@nexign/scc-ui-kit';
import find from 'lodash/find'
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { icons } from '@/main/assets';
import { ImgStyled } from '@/main/components/common/Block/Img';
import ArrowTooltip from '@/main/components/common/Tooltip/Tooltip';
import { openMobileChangeService } from '@/main/components/Table/actions/tableActions';
import { makeSelectTranslationPageSubscriberMobile } from '@/main/modules/internationalization/intSelectors';
import { AccessComponentKeyEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { setLocalStorageDataAction } from '@/main/modules/storage/storageActions';
import { ELocalStorageKeys } from '@/main/modules/storage/storageTypes';
import { setItemToLocalStorage } from '@/main/modules/storage/storageUtils';
import { ITableButton } from '@/main/modules/table';
import { tableBasketCountSelector, tableFilterSubscribersInRoaming, tableMetaButtonsSelector, tableSubscribersInRoamingWithFilterCount } from '@/main/modules/table/selectors';
import { useSetSubscribersInRoamingMutation } from '@/main/modules/table/store';
import { EPreselectedCategory } from '@/main/pages/PageProducts/tariffsAndServicesConstants';
import { IRouteParams } from '@/main/routes/constants/routesConstants';
import { stringFormatParams } from '@/main/utils/stringFormat';

import { API_SUBSCRIBER_TABLE_META } from '../../constants';
import { 
    RoamSubscribersContainerStyled, 
    RoamSubscribersControlsStyled, 
    RoamSubscribersElementStyled, 
    FastActionButtonStyled,
    IconButtonContainer,
} from '../styled/RoamSubscribersPlateStyled';


export const RoamSubscribersPlate = () => {

    const dispatch = useDispatch();
    const { clientId } = useParams<IRouteParams>();

    const filterSubscribersInRoaming = useSelector(tableFilterSubscribersInRoaming);
    const subscribersInRoamingWithFilterCount = useSelector(tableSubscribersInRoamingWithFilterCount);
    const basketCount = useSelector(tableBasketCountSelector);
    const buttons = useSelector(tableMetaButtonsSelector(API_SUBSCRIBER_TABLE_META));
    const pageTranslation = useSelector(makeSelectTranslationPageSubscriberMobile());
    const { roamSubscribersPlate } = pageTranslation;

    // условия активности кнопки "Подключить" аналогичны кнопке "Услуги и опции" в футере
    const button = 
        find(
            buttons, 
            (buttonItem: ITableButton) => 
                buttonItem.buttonName === AccessComponentKeyEnum.FooterButtonRateServicesAndTariffOptions,
        )
    const buttonMaxCount = button?.maxCount ?? 0;
    const isConnectBtnDisabled = !basketCount || basketCount > buttonMaxCount;
    
    // тексты для кнопок
    const switcherText = useMemo(() => {
        return stringFormatParams(
            roamSubscribersPlate.switcherText, 
            { count: subscribersInRoamingWithFilterCount },
        );
    }, [roamSubscribersPlate.switcherText, subscribersInRoamingWithFilterCount]);
    const connectButtonTooltip = useMemo(() => {
        if (!basketCount) {
            return roamSubscribersPlate.connetButtonTooltips.noSubsSelected;
        } else if (basketCount > buttonMaxCount) {
            return stringFormatParams(
                roamSubscribersPlate.connetButtonTooltips.tooManySelected, 
                { maxCount: buttonMaxCount },
            );
        }
        return '';
    }, [
        basketCount, 
        buttonMaxCount, 
        roamSubscribersPlate.connetButtonTooltips.noSubsSelected, 
        roamSubscribersPlate.connetButtonTooltips.tooManySelected,
    ]);


    const [setSubscribersInRoaming] = useSetSubscribersInRoamingMutation();
    
    const handleToggle = (value: boolean) => {
        setSubscribersInRoaming({ value });
    };
    
    const onCloseClick = () => {
        setSubscribersInRoaming({ value: false });
        dispatch(
            setLocalStorageDataAction({
                key: ELocalStorageKeys.IS_SUBSCRIBERS_IN_ROAMING_PLATE_HIDDEN,
                value: true,
            }),
        );
    };

    const onConnectClick =() => {
        setItemToLocalStorage(ELocalStorageKeys.PRESELECTED_PRODUCT_CATEGORY, EPreselectedCategory.ROAMING_CATEGORY_ID);
        dispatch(openMobileChangeService(clientId ? { clientId } : {}));
    };

    return (
        <RoamSubscribersContainerStyled>
            <RoamSubscribersControlsStyled>
                <RoamSubscribersElementStyled>
                    <Switch 
                        checked={filterSubscribersInRoaming} 
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleToggle(e.target.checked)}
                    />
                    <Typography>{switcherText}</Typography>
                </RoamSubscribersElementStyled>
                {filterSubscribersInRoaming && 
                    <>   
                        <RoamSubscribersElementStyled>
                            <ImgStyled className="medium" src={icons.infoLightBlueIcon} />
                            <Typography variant="body2">
                                {roamSubscribersPlate.infoText}
                            </Typography>
                        </RoamSubscribersElementStyled>
                        <ArrowTooltip
                            placement="top"
                            title={connectButtonTooltip}
                            titleAlign="center"
                        >
                            <div>
                                <FastActionButtonStyled
                                    className="fast-actions-button"
                                    color='primary'
                                    disabled={isConnectBtnDisabled}
                                    size="small"
                                    variant="contained"
                                    onClick={onConnectClick}
                                >
                                    {roamSubscribersPlate.connectButton}
                                </FastActionButtonStyled>
                            </div>
                        </ArrowTooltip>
                    </>
                }
            </RoamSubscribersControlsStyled>
            <IconButtonContainer>
                <ArrowTooltip
                    placement="top-end"
                    title={roamSubscribersPlate.closeButtonTooltip}
                    titleAlign="center"
                >
                    <div>
                        <IconButton onClick={onCloseClick}>
                            <ImgStyled className="small" src={icons.cancelMiddleIcon} />
                        </IconButton>
                    </div>
                </ArrowTooltip>
                </IconButtonContainer>
        </RoamSubscribersContainerStyled>
    );
};
